import React from "react";
import { Dialog, DialogHandler, FullPageLoader, FullPageLoaderHandler } from "cakemail-ui-components/lib/react";
import { getVerisoulAccount } from "../services/accountService";
import { I18n, Translate } from "react-redux-i18n";
import { getUrlsBasedOnLanguage } from "./languages";
import store from "../store";
import { sendZendeskRequestService } from "../services/hiddenService";
import { SUPPORT_TYPE } from "../components/generalDialogs/simpleDialogs";
import { FEATURE_FLAG, hasFeatureFlag } from "./LimitsAndFeatures";
import { amIImpersonating, isPartnerOrOrganization, openBillingPortal } from "./apiUtils";
import { getPlanName } from "./billingUtils";
import { SEGMENT_EVENTS, trackSegmentEvent } from "../const/SegmentEvents";
import fetchRoute from "./routes";

//Data Candy
const static_verisouls_blacklist = ["1-210699"];

//options : removeCancel, action
export function verifyVerisoulAccount(options = { id: "", action: "", primaryActionCallback: undefined }, successCallback, errorCallback) {
	if (!amIImpersonating() && hasFeatureFlag(FEATURE_FLAG.VERISOUL) && !hasFeatureFlag(FEATURE_FLAG.VERISOUL_DRYRUN)) {
		const account = store.getState().global.account;
		const hasBlacklistMatch = static_verisouls_blacklist.find((l) => {
			return account?.lineage?.startsWith(l);
		});
		if (amIImpersonating() || (account && hasBlacklistMatch)) {
			successCallback && successCallback({ fake: false });
			return;
		}
		getVerisoulAccount(options, (data) => {
			let verdict = getVerisoulVerdict(data);

			if (verdict.fake && verdict.verdicts.multiAccount) {
				showVerisoulDialog(verdict, options);
			} else {
				successCallback && successCallback(verdict);
			}
		}, errorCallback);
	} else {
		successCallback && successCallback({ fake: false });
	}
}

export function getVerisoulVerdict(verisoulData) {
	const customer = store.getState().global.customer;
	let planName = getPlanName(customer);

	let verisoulAccount = verisoulData.data;

	const fakeAccount = verisoulAccount.decision === "Fake";
	const accountsLinked = verisoulAccount.accounts_linked;
	const metaData = verisoulAccount.account.metadata;
	//if you have linked account
	const linkedAccount = verisoulAccount.linked_account;
	//Later business logic to do with those
	const riskScores = verisoulAccount.risk_signal_average;

	let multiAccount = false;

	if (fakeAccount) {
		if (accountsLinked > 0) {
			//Check only average of linked account for now
			const totalScore = linkedAccount.reduce((total, a) => total + a.score, 0);
			multiAccount = (totalScore * 100 / accountsLinked) >= 40;
			let freeAccounts = 0;
			for (const lA of linkedAccount) {
				const lAMeta = lA.metadata;
				if (["no_subscription", "free"].includes(lAMeta?.billing?.plan)) {
					freeAccounts += 1;
				}
			}

			if ((customer && (["free", "no_subscription"].includes(planName)) || (!customer && ["no_subscription", "free"].includes(metaData?.billing?.plan)))) {
				freeAccounts += 1;
			} else {
				// If the current account is not free or without subscription, then bypass the dialog
				multiAccount = false;
			}

			multiAccount = multiAccount && freeAccounts > 1;
		}

	}

	return {
		fake: fakeAccount,
		verdicts: {
			multiAccount
		}
	};
}


export function showVerisoulDialog(verdict, options = { removeCancel: false, action: "", primaryActionCallback: undefined }) {
	trackSegmentEvent(SEGMENT_EVENTS.BUTTON_CLICKED, {
		context: "Verisoul Dialog Showed",
		extendedContext: {
			action: options?.action
		},
	});

	let text = "";
	let title = "app-verisoul-general-dialog-title";
	let action = "app-verisoul-dialog-action-continue";
	let showBillingBtn = false;
	const supportText = `User is suppected of fake multi-accounts.
Check Verisoul data at https://dashboard.verisoul.ai/ and tag user as main_account to remove multi_accounts score if it is a false positive.
`;
	let primaryBtnText = "app-general-request-support-btn";

	//Dialog content
	if (verdict.verdicts.multiAccount) {
		text = "app-verisoul-dialog-multi-account";
	}

	if (hasFeatureFlag("stripe_portal_access") && !isPartnerOrOrganization()) {
		showBillingBtn = true;
		primaryBtnText = "app-menu-account-usage-manage_your_plan";
	}

	//Action text context
	if (options?.action === "activateAutomation") {
		action = "app-verisoul-dialog-action-activateAutomation";
	} else if (options?.action === "sendTest") {
		action = "app-verisoul-dialog-action-sendTest";
	} else if (options?.action === "sendCampaign") {
		action = "app-verisoul-dialog-action-sendCampaign";
	} else if (options?.action === "accountConfirm") {
		showBillingBtn = false;
		title = "app-verisoul-general-dialog-title-account-confirm";
		text = "app-verisoul-dialog-multi-account-account-confirm";
		primaryBtnText = "app-auth-sign_btn";
	}

	const dialogConfig = {
		title: <Translate value={title} />,
		content: <p>
			<Translate value={text} action={I18n.t(action)} dangerousHTML />
			{showBillingBtn &&
				<>
					<br /><br />
					<a onClick={(e) => {
						e.preventDefault();
						trackSegmentEvent(SEGMENT_EVENTS.BUTTON_CLICKED, {
							context: "Verisoul Dialog Requested Support",
							extendedContext: {
								action: options?.action
							},
						});
						DialogHandler.hide();
						sendZendeskRequestService({
							content: {
								type: SUPPORT_TYPE.SUPPORT,
								message: supportText
							}
						});
					}}><Translate value={"app-support-request-assistance-txt"} /></a>
				</>
			}
		</p >,
		primaryBtnText: <Translate value={primaryBtnText} />,
		primaryBtnOnClick: () => {
			options.primaryActionCallback && options.primaryActionCallback();
			DialogHandler.hide();
			if (options?.action === "accountConfirm") {
				FullPageLoaderHandler.hide();
				trackSegmentEvent(SEGMENT_EVENTS.BUTTON_CLICKED, {
					context: "Verisoul Dialog Account Confirm SignIn",
					extendedContext: {
						action: options?.action
					},
				});
				window.location = "#" + fetchRoute("signIn");
			} else if (showBillingBtn) {
				trackSegmentEvent(SEGMENT_EVENTS.BUTTON_CLICKED, {
					context: "Verisoul Dialog Upgrade Plan",
					extendedContext: {
						action: options?.action
					},
				});
				openBillingPortal();
			} else {
				trackSegmentEvent(SEGMENT_EVENTS.BUTTON_CLICKED, {
					context: "Verisoul Dialog Requested Support",
					extendedContext: {
						action: options?.action
					},
				});
				sendZendeskRequestService({
					content: {
						type: SUPPORT_TYPE.SUPPORT,
						message: supportText
					}
				});
			}
		},
		secondaryBtnText: options.action === "accountConfirm" ? undefined : <Translate value="app-general-cancel" />,
		secondaryBtnOnClick: () => {
			if (options.action === "accountConfirm") {
				FullPageLoaderHandler.hide();
				window.location = "#" + fetchRoute("signIn");
			}

			trackSegmentEvent(SEGMENT_EVENTS.BUTTON_CLICKED, {
				context: "Verisoul Dialog Cancel",
				extendedContext: {
					action: options?.action
				},
			});

			DialogHandler.hide();
		}
	};

	if (options.removeCancel) {
		delete dialogConfig.secondaryBtnOnClick;
		delete dialogConfig.secondaryBtnText;
	}

	DialogHandler.show(dialogConfig);
}